import React, { useCallback, useState, createContext, useContext } from 'react';
import { CheckCircleIcon, XCircleIcon, AlertTriangleIcon, InfoIcon, XIcon } from 'lucide-react';
export type ToastType = 'success' | 'error' | 'warning' | 'info';
export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}
interface ToastContextType {
  addToast: (message: string, type?: ToastType) => void;
  removeToast: (id: string) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
export function ToastProvider({
  children
}: {
  children: ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);
  const addToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, {
      id,
      message,
      type
    }]);
    setTimeout(() => {
      removeToast(id);
    }, 3000);
  }, [removeToast]);
  return <ToastContext.Provider value={{
    addToast,
    removeToast
  }} data-id="element-90">
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-91">
        {toasts.map(toast => <ToastItem key={toast.id} toast={toast} onRemove={() => removeToast(toast.id)} data-id="element-92" />)}
      </div>
    </ToastContext.Provider>;
}
function ToastItem({
  toast,
  onRemove
}: {
  toast: Toast;
  onRemove: () => void;
}) {
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-[#00FF88]" data-id="element-93" />;
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-[#FF3B3B]" data-id="element-94" />;
      case 'warning':
        return <AlertTriangleIcon className="w-5 h-5 text-[#FFB800]" data-id="element-95" />;
      case 'info':
        return <InfoIcon className="w-5 h-5 text-[#00D4FF]" data-id="element-96" />;
    }
  };
  const getBorderColor = () => {
    switch (toast.type) {
      case 'success':
        return 'border-[#00FF88]/50';
      case 'error':
        return 'border-[#FF3B3B]/50';
      case 'warning':
        return 'border-[#FFB800]/50';
      case 'info':
        return 'border-[#00D4FF]/50';
    }
  };
  return <div className={`flex items-center gap-3 bg-[#111827] border ${getBorderColor()} shadow-lg rounded p-4 pointer-events-auto animate-in slide-in-from-right-5 fade-in duration-300 min-w-[300px] max-w-md`} data-id="element-97">
      {getIcon()}
      <p className="text-sm text-[#E2E8F0] flex-1" data-id="element-98">{toast.message}</p>
      <button onClick={onRemove} className="text-[#94A3B8] hover:text-[#E2E8F0] transition-colors" data-id="element-99">
        <XIcon className="w-4 h-4" data-id="element-100" />
      </button>
    </div>;
}